<template>
    <div class="product-prices-input">
        <div class="form-group form-inline">
            <label class="col-sm-6 col-md-3 justify-content-start" for="ref_amount">Prix de référence</label>
            <div class="input-group col-sm-6 col-md-4 col-lg-3">
                <input class="form-control" name="ref_amount" id="ref_amount" type="number" step="0.01" v-model="lRefAmount" required>
                <div class="input-group-append">
                    <span class="input-group-text">{{ lRefAmountHasTaxIncl ? 'TTC' : 'HT' }}</span>
                </div>
            </div>

            <div class="mt-1 col-sm-6 col-md-4 offset-sm-6 offset-md-0 form-inline">
                <label class="mr-1">Montant exprimé</label>
                <div class="btn-group btn-group-toggle" data-toggle="buttons">
                    <label class="btn btn-outline-secondary">
                        <input type="radio" name="ref_amount_has_taxes_incl" v-model="lRefAmountHasTaxIncl" :value="0"> HT
                    </label>
                    <label class="btn btn-outline-secondary">
                        <input type="radio" name="ref_amount_has_taxes_incl" v-model="lRefAmountHasTaxIncl" :value="1"> TTC
                    </label>
                </div>
            </div>
        </div>

        <hr class="d-md-none">

        <div class="form-group form-inline">
            <label class="col-sm-6 col-md-3 justify-content-start" for="taxSelector">Taux TVA</label>
            <div class="input-group col-sm-6 col-md-3">
                <select class="form-control" id="taxSelector" v-model="lTax">
                    <option v-for="tax in taxes" :value="tax">{{ tax.rate }}%</option>
                </select>
            </div>
            <input type="hidden" name="tax" :value="lTax.id">
        </div>

        <hr class="d-md-none">

        <div v-for="priceCategory in pricesCategories" :key="priceCategory.id">
            <div class="form-group form-inline">
                <label class="col-sm-6 col-md-4 justify-content-start" :for="'prices_' + priceCategory.id">
                    {{ priceCategory.name }}
                    <template v-if="priceCategory.percentage_to_apply != 0">
                        ({{ priceCategory.percentage_to_apply }}%)
                    </template>
                </label>
                <div class="input-group col-sm-6 col-md-4 col-lg-3">
                    <input class="form-control" type="number" step="0.01" :id="'prices_' + priceCategory.id" :value="priceCategory.has_taxes_inc ? pricesBasedOnRefAmount[priceCategory.id].amountTaxesInc : pricesBasedOnRefAmount[priceCategory.id].amountTaxesFree" readonly>
                    <div class="input-group-append">
                        <span class="input-group-text">{{ priceCategory.has_taxes_inc ? 'TTC' : 'HT' }}</span>
                    </div>
                </div>
                <template v-if="priceCategory.price_editable">
                    <div class="input-group offset-sm-6 col-sm-6 offset-md-0 col-md-4 col-lg-3 mt-1">
                        <div class="input-group-prepend">
                            <button class="btn btn-outline-secondary" type="button" @click="resetForcedPrice(priceCategory)">
                                <i class="fad fa-arrow-right-from-line"></i>
                            </button>
                        </div>
                        <input class="form-control" type="number" step="0.01" v-model="lForcedPrices[priceCategory.id]">
                        <div class="input-group-append">
                            <span class="input-group-text">{{ priceCategory.has_taxes_inc ? 'TTC' : 'HT' }}</span>
                        </div>
                    </div>
                </template>
            </div>

            <hr class="d-md-none">
        </div>

        <input type="hidden" name="prices" v-model="pricesJson">
    </div>
</template>

<script>
import {findIndex, first} from 'lodash';
import currency from 'currency.js';

export default {
    name: 'ProductPricesInput',

    props: {
        taxes: {
            type: Array,
            required: true,
        },
        pricesCategories: {
            type: Array,
            required: true,
        },
        value: {
            type: Object,
            required: false,
        }
    },

    data() {
        const lForcedPrices = {};
        for (const pc of this.pricesCategories) {
            lForcedPrices[pc.id] = null;
        }

        let prefillTax = first(this.taxes);

        if (this.value) {
            if (this.value.tax || this.value.taxrate) {
                const taxIndex = this.value.tax ? findIndex(this.taxes, {id: this.value.tax}) : findIndex(this.taxes, {rate: this.value.taxrate});
                if (taxIndex >= 0) {
                    prefillTax = this.taxes[taxIndex];
                }
            }

            if (this.value.prices) {
                for (const pc of this.pricesCategories) {
                    if (pc.price_editable) {
                        let priceToForce = null;
                        if (this.value.prices[pc.id]) {
                            priceToForce = pc.has_taxes_inc
                                ? (this.value.prices[pc.id]['amountTaxesInc'] || null)
                                : (this.value.prices[pc.id]['amountTaxesFree'] || null)
                        }

                        if (priceToForce !== null) {
                            lForcedPrices[pc.id] = priceToForce;
                        }
                    }
                }
            }
        }

        return {
            lRefAmount: this.value ? this.value.ref_amount : null,
            lRefAmountHasTaxIncl: this.value ? (this.value.ref_amount_has_taxes_incl ? 1 : 0) : 0,
            lTax: prefillTax,
            lForcedPrices,
        }
    },

    computed: {
        pricesBasedOnRefAmount() {
            const prices = {};
            const refAmountCurrency = currency(this.lRefAmount);

            const refAmountTaxFree = this.lRefAmountHasTaxIncl
                ? this.subTaxFrom(refAmountCurrency, this.lTax.rate)
                : refAmountCurrency;

            const refAmountTaxInc = this.lRefAmountHasTaxIncl
                ? refAmountCurrency
                : this.addTaxTo(refAmountCurrency, this.lTax.rate);

            for (const pc of this.pricesCategories) {
                const percentageToApply = this.pricesCategoriesById[pc.id].percentage_to_apply || 0;

                const priceTaxFreeWithPercentApplied = percentageToApply == 0
                    ? refAmountTaxFree
                    : this.addTaxTo(refAmountTaxFree, percentageToApply);

                const priceTaxInc = percentageToApply == 0
                    ? refAmountTaxInc
                    : this.addTaxTo(priceTaxFreeWithPercentApplied, this.lTax.rate);

                prices[pc.id] = {
                    name: this.pricesCategoriesById[pc.id].name,
                    taxid: this.lTax.id,
                    taxrate: this.lTax.rate,
                    hasTaxesInc: this.pricesCategoriesById[pc.id].has_taxes_inc,
                    amountTaxesFree: `${priceTaxFreeWithPercentApplied}`,
                    amountTaxesInc: `${priceTaxInc}`,
                };
            }
            return prices;
        },

        pricesWithForcedPrices() {
            const pricesWithForcedPrices = {};
            for (const pcId in this.pricesBasedOnRefAmount) {
                const finalPrice = {...this.pricesBasedOnRefAmount[pcId]};

                if (this.lForcedPrices[pcId]) {
                    if (finalPrice.hasTaxesInc) {
                        finalPrice['amountTaxesInc'] = this.lForcedPrices[pcId];
                        finalPrice['amountTaxesFree'] = `${this.subTaxFrom(this.lForcedPrices[pcId], this.pricesBasedOnRefAmount[pcId].taxrate)}`;
                    } else {
                        finalPrice['amountTaxesInc'] = `${this.addTaxTo(this.lForcedPrices[pcId], this.pricesBasedOnRefAmount[pcId].taxrate)}`;
                        finalPrice['amountTaxesFree'] = this.lForcedPrices[pcId];
                    }
                }
                pricesWithForcedPrices[pcId] = finalPrice;
            }
            return pricesWithForcedPrices;
        },

        pricesJson() {
            return JSON.stringify(this.pricesWithForcedPrices);
        },

        pricesCategoriesById() {
            const pricesCategoriesById = {};
            for (const pc of this.pricesCategories) {
                pricesCategoriesById[pc.id] = pc;
            }
            return pricesCategoriesById;
        },
    },

    methods: {
        resetForcedPrice(priceCategory) {
            this.lForcedPrices[priceCategory.id] = priceCategory.has_taxes_inc
                ? this.pricesBasedOnRefAmount[priceCategory.id].amountTaxesInc
                : this.pricesBasedOnRefAmount[priceCategory.id].amountTaxesFree;
        },

        addTaxTo(price, taxrate) {
            return currency(price)
                .multiply(
                    currency(taxrate, {precision: 4})
                        .add(100)
                        .divide(100)
                );
        },

        subTaxFrom(price, taxrate) {
            return currency(price)
                .divide(
                    currency(taxrate, {precision: 4})
                        .add(100)
                        .divide(100)
                );
        },
    },
}
</script>

<style lang="scss" scoped>

</style>
